import React from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import type {
  NotificationOptimization,
  EngagementPattern,
  TimingPreference,
} from '@coffeeplexity/shared';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { styles } from './NotificationOptimizationDisplay.styles';

interface NotificationOptimizationDisplayProps {
  optimization: NotificationOptimization;
  engagementPatterns: EngagementPattern[];
  timingPreferences: TimingPreference[];
  onClose: () => void;
  onPreferencesPress: () => void;
}

export const NotificationOptimizationDisplay: React.FC<
  NotificationOptimizationDisplayProps
> = ({
  optimization,
  engagementPatterns,
  timingPreferences,
  onClose,
  onPreferencesPress,
}) => {
  // Format optimal times
  const formatOptimalTimes = (times: string[]) => {
    return times
      .map(time => {
        const hour = parseInt(time.split(':')[0]);
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        return `${displayHour}${period}`;
      })
      .join(', ');
  };

  // Get engagement rate color
  const getEngagementRateColor = (rate: number) => {
    if (rate >= 0.7) return '#27AE60';
    if (rate >= 0.4) return '#F39C12';
    return '#E74C3C';
  };

  // Calculate overall optimization score
  const optimizationScore = Math.round(
    (optimization.engagementThreshold + optimization.adaptationRate) * 50
  );

  // Get best engagement times
  const bestTimes = timingPreferences
    .filter(pref => pref.engagementRate > 0.3 && pref.sampleSize >= 5)
    .sort((a, b) => b.engagementRate - a.engagementRate)
    .slice(0, 3);

  // Get engagement patterns summary
  const patternsSummary = engagementPatterns.reduce(
    (acc, pattern) => {
      if (pattern.strength > 0.5) {
        acc[pattern.patternType] = (acc[pattern.patternType] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <View style={styles.container}>
      <View style={styles.overlay}>
        <View style={styles.modal}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>AI Optimization Insights</Text>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Text style={styles.closeButtonText}>×</Text>
            </TouchableOpacity>
          </View>

          <ScrollView
            style={styles.content}
            showsVerticalScrollIndicator={false}
          >
            {/* Overall Score */}
            <View style={styles.scoreSection}>
              <View style={styles.scoreContainer}>
                <ConfidenceIndicator
                  confidence={optimizationScore / 100}
                  size='large'
                  showLabel={false}
                />
                <View style={styles.scoreText}>
                  <Text style={styles.scoreValue}>{optimizationScore}%</Text>
                  <Text style={styles.scoreLabel}>Optimized</Text>
                </View>
              </View>
              <Text style={styles.scoreDescription}>
                Your notification timing and content are being continuously
                optimized based on your engagement patterns.
              </Text>
            </View>

            {/* Optimal Times */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>
                🕐 Best Times to Reach You
              </Text>
              <View style={styles.timesContainer}>
                {optimization.optimalTimes.length > 0 ? (
                  <Text style={styles.timesText}>
                    {formatOptimalTimes(optimization.optimalTimes)}
                  </Text>
                ) : (
                  <Text style={styles.noDataText}>
                    Still learning your preferences...
                  </Text>
                )}
              </View>
              {bestTimes.length > 0 && (
                <View style={styles.timingDetails}>
                  {bestTimes.map((time, index) => (
                    <View key={index} style={styles.timingItem}>
                      <Text style={styles.timingTime}>{time.hourOfDay}:00</Text>
                      <View style={styles.timingRate}>
                        <Text
                          style={[
                            styles.timingRateText,
                            {
                              color: getEngagementRateColor(
                                time.engagementRate
                              ),
                            },
                          ]}
                        >
                          {Math.round(time.engagementRate * 100)}%
                        </Text>
                        <Text style={styles.timingRateLabel}>engagement</Text>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>

            {/* Quiet Hours */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>🔕 Quiet Hours</Text>
              {optimization.quietHours.enabled ? (
                <Text style={styles.quietHoursText}>
                  {optimization.quietHours.startTime} -{' '}
                  {optimization.quietHours.endTime}
                </Text>
              ) : (
                <Text style={styles.noDataText}>Not configured</Text>
              )}
            </View>

            {/* Engagement Patterns */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>📊 Your Patterns</Text>
              {Object.keys(patternsSummary).length > 0 ? (
                <View style={styles.patternsContainer}>
                  {Object.entries(patternsSummary).map(([type, count]) => (
                    <View key={type} style={styles.patternItem}>
                      <Text style={styles.patternType}>
                        {type.replace(/_/g, ' ').toUpperCase()}
                      </Text>
                      <Text style={styles.patternCount}>{count} patterns</Text>
                    </View>
                  ))}
                </View>
              ) : (
                <Text style={styles.noDataText}>
                  Building your engagement profile...
                </Text>
              )}
            </View>

            {/* Channel Preferences */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>📱 Channel Preferences</Text>
              <View style={styles.channelsContainer}>
                {optimization.channelPreferences.map((channel, index) => (
                  <View key={index} style={styles.channelItem}>
                    <Text style={styles.channelName}>
                      {channel.channel.toUpperCase()}
                    </Text>
                    <View
                      style={[
                        styles.channelStatus,
                        channel.enabled
                          ? styles.channelEnabled
                          : styles.channelDisabled,
                      ]}
                    >
                      <Text
                        style={[
                          styles.channelStatusText,
                          channel.enabled
                            ? styles.channelEnabledText
                            : styles.channelDisabledText,
                        ]}
                      >
                        {channel.enabled ? 'ON' : 'OFF'}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>

            {/* Optimization Settings */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>⚙️ AI Settings</Text>
              <View style={styles.settingsContainer}>
                <View style={styles.settingItem}>
                  <Text style={styles.settingLabel}>Frequency Limit</Text>
                  <Text style={styles.settingValue}>
                    {optimization.frequencyLimit} per day
                  </Text>
                </View>
                <View style={styles.settingItem}>
                  <Text style={styles.settingLabel}>Engagement Threshold</Text>
                  <Text style={styles.settingValue}>
                    {Math.round(optimization.engagementThreshold * 100)}%
                  </Text>
                </View>
                <View style={styles.settingItem}>
                  <Text style={styles.settingLabel}>Adaptation Rate</Text>
                  <Text style={styles.settingValue}>
                    {Math.round(optimization.adaptationRate * 100)}%
                  </Text>
                </View>
              </View>
            </View>

            {/* Last Optimized */}
            <View style={styles.section}>
              <Text style={styles.lastOptimizedText}>
                Last optimized:{' '}
                {new Date(optimization.lastOptimized).toLocaleDateString()}
              </Text>
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity
              style={styles.preferencesButton}
              onPress={onPreferencesPress}
            >
              <Text style={styles.preferencesButtonText}>
                Customize Preferences
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
};
